<template>
  <div id="subscription_detail">
    <Navbar current="Detalhes da Assinatura" />
    <Sidebar />
    <Container>
      <Header title="Detalhes da Assinatura">
        <template v-slot:button>
          <div class="d-flex gap-2">
            <button
              v-if="canGrantDays"
              class="btn btn-info text-white"
              @click="showGrantDaysModal = true"
            >
              <i class="fa-solid fa-calendar-plus me-1"></i>
              Dar Dias Grátis
            </button>
            <button
              v-if="canGrantFreeMonth"
              class="btn btn-success"
              @click="showGrantFreeMonthModal = true"
            >
              <i class="fa-solid fa-gift me-1"></i>
              Dar Mês Grátis
            </button>
            <button
              v-if="canGrantCredit"
              class="btn btn-warning"
              @click="showGrantCreditModal = true"
            >
              <i class="fa-solid fa-coins me-1"></i>
              Dar Crédito
            </button>
            <a
              v-if="item.subscription_id"
              :href="stripeSubscriptionLink()"
              target="_blank"
              class="btn btn-primary"
            >
              <i class="fa-brands fa-stripe me-1"></i>
              Ver no Stripe
            </a>
            <button
              v-if="canCancelSubscription"
              class="btn btn-danger"
              @click="showCancelModal = true"
            >
              <i class="fa-solid fa-ban me-1"></i>
              Cancelar Assinatura
            </button>
          </div>
        </template>
      </Header>
      <LoadingContainer v-if="loadingItems" />
      <div v-else>
        <div class="row g-4 mb-4">
          <!-- Info do Psicólogo -->
          <div class="col-12 col-lg-6" v-if="item.user">
            <div class="card h-100">
              <div class="card-header d-flex align-items-center justify-content-between">
                <h6 class="mb-0">
                  <i class="fa-solid fa-user me-2"></i>Psicólogo
                </h6>
                <router-link
                  :to="`${$paths.psychologists.detail}/${item.user.id}`"
                  class="btn btn-sm btn-outline-primary"
                >
                  <i class="fa-solid fa-external-link me-1"></i>
                  Ver Psicólogo
                </router-link>
              </div>
              <div class="card-body">
                <div class="d-flex align-items-center gap-3 mb-3">
                  <img
                    v-if="item.user.profile_image"
                    :src="$baseImageUrl + item.user.profile_image"
                    class="rounded-circle flex-shrink-0"
                    width="64"
                    height="64"
                    style="object-fit: cover"
                  />
                  <div
                    v-else
                    class="rounded-circle bg-secondary d-flex align-items-center justify-content-center flex-shrink-0"
                    style="width: 64px; height: 64px"
                  >
                    <i class="fa-solid fa-user text-white fa-lg"></i>
                  </div>
                  <div class="overflow-hidden flex-grow-1">
                    <h5 class="mb-1 text-truncate">{{ item.user.name }}</h5>
                    <small class="text-muted text-truncate d-block">
                      <i class="fa-solid fa-envelope me-1"></i>{{ item.user.email }}
                    </small>
                  </div>
                </div>
                <div class="row g-2">
                  <div class="col-6" v-if="item.user.phone">
                    <small class="text-muted d-block">Telefone</small>
                    <span>{{ item.user.country_code }} {{ item.user.phone }}</span>
                  </div>
                  <div class="col-6" v-if="item.user.crp">
                    <small class="text-muted d-block">CRP</small>
                    <span>{{ item.user.crp }}</span>
                  </div>
                  <div class="col-6" v-if="item.user.address_city">
                    <small class="text-muted d-block">Localização</small>
                    <span>{{ item.user.address_city }}{{ item.user.address_state ? ', ' + item.user.address_state : '' }}</span>
                  </div>
                  <div class="col-6" v-if="item.user.customer_id">
                    <small class="text-muted d-block">ID Stripe</small>
                    <a
                      :href="getCustomerStripeLink(item.user.customer_id)"
                      target="_blank"
                      class="font-monospace small text-truncate d-block"
                      :title="`Ver ${item.user.customer_id} no Stripe`"
                    >
                      <i class="fa-brands fa-stripe me-1"></i>{{ item.user.customer_id }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Info da Assinatura -->
          <div :class="item.user ? 'col-12 col-lg-6' : 'col-12'">
            <div class="card h-100">
              <div class="card-header d-flex align-items-center justify-content-between">
                <h6 class="mb-0">
                  <i class="fa-solid fa-credit-card me-2"></i>Informações da Assinatura
                </h6>
                <span class="badge" :class="statusBadgeClass()">{{ item.formatted_status }}</span>
              </div>
              <div class="card-body">
                <div class="row g-3">
                  <div class="col-6">
                    <small class="text-muted d-block">Plano</small>
                    <strong :class="{ 'text-plus': isPlusPlan }">{{ item.formatted_plan_frequency || '-' }}</strong>
                  </div>
                  <div class="col-6">
                    <small class="text-muted d-block">Valor</small>
                    <strong :class="{ 'text-plus': isPlusPlan }">{{ item.formatted_unit_amount }}</strong>
                  </div>
                  <div class="col-6">
                    <small class="text-muted d-block">Início</small>
                    <span>{{ item.formatted_start_date }}</span>
                  </div>
                  <div class="col-6">
                    <small class="text-muted d-block">Criado em</small>
                    <span>{{ item.formatted_created_at }}</span>
                  </div>
                  <div class="col-12">
                    <div class="period-card">
                      <small class="text-muted d-block mb-2">
                        <i class="fa-solid fa-calendar-days me-1"></i>Período Atual
                      </small>
                      <div class="row g-2">
                        <div class="col-6">
                          <small class="text-muted d-block">Início</small>
                          <span>{{ item.formatted_current_period_start }}</span>
                        </div>
                        <div class="col-6">
                          <small class="text-muted d-block">Fim</small>
                          <span>{{ item.formatted_current_period_end }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="col-12" v-if="item.canceled_at">
                    <small class="text-muted d-block">Cancelamento</small>
                    <span class="text-danger">{{ item.formatted_canceled_at }}</span>
                  </div>
                  <div class="col-12" v-if="item.subscription_id">
                    <small class="text-muted d-block">ID Stripe</small>
                    <a
                      :href="stripeSubscriptionLink()"
                      target="_blank"
                      class="font-monospace small"
                      :title="`Ver ${item.subscription_id} no Stripe`"
                    >
                      <i class="fa-brands fa-stripe me-1"></i>{{ item.subscription_id }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Próxima Fatura -->
        <div class="card mb-4" v-if="item.upcoming_invoice">
          <div class="card-header d-flex align-items-center justify-content-between">
            <h6 class="mb-0">
              <i class="fa-solid fa-clock me-2 text-primary"></i>Próxima Fatura
              <span class="badge bg-primary ms-2">Prevista</span>
            </h6>
            <span v-if="item.upcoming_invoice.has_credits" class="badge bg-success">
              <i class="fa-solid fa-tag me-1"></i>
              Com crédito aplicado
            </span>
          </div>
          <div class="card-body">
            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <div class="info-box">
                  <small class="text-muted d-block">Data prevista</small>
                  <strong>
                    <i class="fa-solid fa-calendar me-1"></i>
                    {{ item.upcoming_invoice.formatted_date }}
                  </strong>
                </div>
              </div>
              <div class="col-md-6">
                <div class="info-box">
                  <small class="text-muted d-block">Total a pagar</small>
                  <strong class="fs-5">{{ item.upcoming_invoice.formatted_amount }}</strong>
                </div>
              </div>
            </div>

            <!-- Breakdown -->
            <div v-if="item.upcoming_invoice.lines && item.upcoming_invoice.lines.length > 0" class="breakdown">
              <div class="breakdown-title">
                <i class="fa-solid fa-list me-2"></i>Detalhamento
              </div>
              <div class="table-responsive">
                <table class="table table-sm mb-0">
                  <tbody>
                    <tr v-for="(line, idx) in item.upcoming_invoice.lines" :key="idx">
                      <td>
                        <i
                          v-if="line.is_credit"
                          class="fa-solid fa-tag text-success me-2"
                        ></i>
                        <i v-else class="fa-solid fa-receipt text-muted me-2"></i>
                        {{ line.description }}
                      </td>
                      <td class="text-end" :class="{ 'text-success': line.is_credit }">
                        <strong>{{ line.formatted_amount }}</strong>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr v-if="item.upcoming_invoice.subtotal !== item.upcoming_invoice.total">
                      <td class="text-muted">Subtotal</td>
                      <td class="text-end text-muted">{{ item.upcoming_invoice.formatted_subtotal }}</td>
                    </tr>
                    <tr class="total-row">
                      <td><strong>Total</strong></td>
                      <td class="text-end"><strong>{{ item.upcoming_invoice.formatted_total }}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <div class="text-muted small mt-3">
              <i class="fa-solid fa-info-circle me-1"></i>
              Esta fatura ainda não foi gerada. O valor pode ser alterado caso haja créditos
              ou cupons aplicados antes da data de cobrança.
            </div>
          </div>
        </div>

        <!-- Invoices -->
        <div class="card" v-if="item.invoices && item.invoices.length > 0">
          <div class="card-header">
            <h6 class="mb-0">
              <i class="fa-solid fa-file-invoice me-2"></i>Faturas
              <span class="badge bg-secondary ms-2">{{ item.invoices.length }}</span>
            </h6>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Status</th>
                    <th>Valor</th>
                    <th>Nota Fiscal</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="invoice in item.invoices" :key="invoice.id">
                    <td>{{ invoice.formatted_created }}</td>
                    <td>
                      <span
                        class="badge"
                        :class="invoice.status === 'paid' ? 'bg-success' : 'bg-warning text-dark'"
                      >
                        {{ invoice.formatted_status }}
                      </span>
                    </td>
                    <td>
                      <div v-if="invoice.has_credit_notes">
                        <strong
                          :class="invoice.zeroed_by_credit_note ? 'text-success' : ''"
                        >
                          {{ invoice.formatted_amount_paid }}
                        </strong>
                        <div class="invoice-credit-detail">
                          <small class="text-muted">
                            Original:
                            <span class="text-decoration-line-through">{{ invoice.formatted_total }}</span>
                          </small>
                          <small class="text-success d-block">
                            <i class="fa-solid fa-tag me-1"></i>
                            Crédito aplicado: <strong>{{ invoice.formatted_credit_notes_total }}</strong>
                          </small>
                        </div>
                      </div>
                      <span
                        v-else
                        :class="{ 'text-muted text-decoration-line-through': invoice.refund && invoice.refund.is_full }"
                      >
                        {{ invoice.formatted_total }}
                      </span>
                      <div v-if="invoice.refund" class="refund-line">
                        <i class="fa-solid fa-rotate-left me-1" :class="refundIconClass(invoice.refund)"></i>
                        <span :class="refundTextClass(invoice.refund)">
                          {{ invoice.refund.formatted_status }}
                          <span v-if="invoice.refund.is_partial" class="text-muted">
                            ({{ invoice.refund.formatted_amount }})
                          </span>
                          <span v-if="invoice.refund.formatted_date" class="text-muted">
                            em {{ invoice.refund.formatted_date }}
                          </span>
                        </span>
                      </div>
                    </td>
                    <td>
                      <div v-if="invoice.nf" class="d-flex align-items-center gap-2">
                        <span class="badge" :class="invoice.nf.status_class">
                          {{ invoice.nf.formatted_status }}
                        </span>
                        <span v-if="invoice.nf.invoice_number" class="text-muted small">
                          #{{ invoice.nf.invoice_number }}
                        </span>
                      </div>
                      <span v-else class="text-muted small">—</span>
                    </td>
                    <td class="text-end">
                      <div class="d-inline-flex gap-1 align-items-center">
                        <a
                          v-if="invoice.nf && invoice.nf.invoice_pdf"
                          :href="invoice.nf.invoice_pdf"
                          target="_blank"
                          class="nf-icon-link"
                          title="Baixar PDF"
                        >
                          <i class="fa-solid fa-file-pdf me-1"></i>PDF
                        </a>
                        <a
                          v-if="invoice.nf && invoice.nf.invoice_xml"
                          :href="invoice.nf.invoice_xml"
                          target="_blank"
                          class="nf-icon-link nf-icon-xml"
                          title="Baixar XML"
                        >
                          <i class="fa-solid fa-file-code me-1"></i>XML
                        </a>
                        <a
                          :href="getInvoiceLink(invoice)"
                          target="_blank"
                          class="nf-icon-link nf-icon-stripe"
                          title="Ver no Stripe"
                        >
                          <i class="fa-brands fa-stripe"></i>
                        </a>
                        <button
                          v-if="invoice.can_refund"
                          type="button"
                          class="nf-icon-link nf-icon-refund"
                          title="Reembolsar"
                          @click="openRefundModal(invoice)"
                        >
                          <i class="fa-solid fa-rotate-left me-1"></i>Reembolsar
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal: Dar Dias Grátis -->
      <div
        v-if="showGrantDaysModal"
        class="modal fade show d-block"
        tabindex="-1"
        style="background-color: rgba(0,0,0,0.5)"
      >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                <i class="fa-solid fa-calendar-plus me-2 text-success"></i>
                Dar Dias Grátis
              </h5>
              <button type="button" class="btn-close" @click="closeGrantDaysModal"></button>
            </div>
            <div class="modal-body">
              <div class="alert alert-info mb-3">
                <i class="fa-solid fa-info-circle me-2"></i>
                Esta ação irá <strong>estender o período de trial</strong> da assinatura.
                O usuário não será cobrado durante os dias grátis adicionados.
              </div>
              <div class="mb-3">
                <label class="form-label">Quantos dias grátis?</label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="grantDaysForm.days"
                  min="1"
                  placeholder="Ex: 7"
                />
              </div>
              <div v-if="grantDaysForm.days > 28" class="alert alert-warning mb-3">
                <i class="fa-solid fa-exclamation-triangle me-2"></i>
                <strong>Dica:</strong> Para mais de 28 dias, considere usar
                <strong>"Dar Mês Grátis"</strong> que aplica um cupom de 100% de desconto.
              </div>
              <div v-if="grantDaysForm.days > 0" class="text-muted small">
                <i class="fa-solid fa-calendar me-1"></i>
                O trial será estendido até aproximadamente
                <strong>{{ getNewTrialEndDate(grantDaysForm.days) }}</strong>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeGrantDaysModal">
                Cancelar
              </button>
              <button
                type="button"
                class="btn btn-success"
                :disabled="!grantDaysForm.days || grantDaysForm.days < 1 || submittingGrantDays"
                @click="handleGrantDays"
              >
                <span v-if="submittingGrantDays">
                  <i class="fa-solid fa-spinner fa-spin me-1"></i> Processando...
                </span>
                <span v-else>
                  <i class="fa-solid fa-check me-1"></i> Confirmar
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal: Dar Mês Grátis -->
      <div
        v-if="showGrantFreeMonthModal"
        class="modal fade show d-block"
        tabindex="-1"
        style="background-color: rgba(0,0,0,0.5)"
      >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                <i class="fa-solid fa-gift me-2 text-success"></i>
                Dar Mês Grátis
              </h5>
              <button type="button" class="btn-close" @click="closeGrantFreeMonthModal"></button>
            </div>
            <div class="modal-body">
              <div class="alert alert-info mb-3">
                <i class="fa-solid fa-info-circle me-2"></i>
                Esta ação irá aplicar um <strong>cupom de 100% de desconto</strong> na assinatura.
                O usuário não pagará pelo(s) próximo(s) mês(es).
              </div>
              <div class="mb-3">
                <label class="form-label">Quantos meses grátis?</label>
                <select class="form-select" v-model.number="grantFreeMonthForm.duration_in_months">
                  <option :value="1">1 mês</option>
                  <option :value="2">2 meses</option>
                  <option :value="3">3 meses</option>
                  <option :value="6">6 meses</option>
                  <option :value="12">12 meses</option>
                </select>
              </div>
              <div class="text-muted small">
                <i class="fa-solid fa-receipt me-1"></i>
                O desconto será aplicado nas próximas
                <strong>{{ grantFreeMonthForm.duration_in_months }}</strong> fatura(s).
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeGrantFreeMonthModal">
                Cancelar
              </button>
              <button
                type="button"
                class="btn btn-success"
                :disabled="submittingGrantFreeMonth"
                @click="handleGrantFreeMonth"
              >
                <span v-if="submittingGrantFreeMonth">
                  <i class="fa-solid fa-spinner fa-spin me-1"></i> Processando...
                </span>
                <span v-else>
                  <i class="fa-solid fa-check me-1"></i> Confirmar
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal: Reembolsar fatura -->
      <div
        v-if="showRefundModal"
        class="modal fade show d-block"
        tabindex="-1"
        style="background-color: rgba(0,0,0,0.5)"
      >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                <i class="fa-solid fa-rotate-left me-2 text-info"></i>
                Reembolsar Fatura
              </h5>
              <button type="button" class="btn-close" @click="closeRefundModal"></button>
            </div>
            <div class="modal-body" v-if="refundForm.invoice">
              <div class="alert alert-info mb-3">
                <i class="fa-solid fa-info-circle me-2"></i>
                Esta ação irá processar um <strong>reembolso total no Stripe</strong> para esta fatura.
                O valor será estornado para o método de pagamento original do usuário.
              </div>
              <div class="refund-summary mb-3">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <small class="text-muted d-block">Fatura</small>
                    <strong>{{ refundForm.invoice.formatted_created }}</strong>
                  </div>
                  <div class="text-end">
                    <small class="text-muted d-block">Valor a reembolsar</small>
                    <strong class="fs-5 text-info">
                      {{ refundForm.invoice.formatted_amount_paid || refundForm.invoice.formatted_total }}
                    </strong>
                  </div>
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label d-flex justify-content-between">
                  <span>Motivo (opcional)</span>
                  <small class="text-muted">{{ (refundForm.reason || '').length }}/500</small>
                </label>
                <textarea
                  class="form-control"
                  rows="3"
                  v-model="refundForm.reason"
                  placeholder="Ex: Pedido do paciente"
                  maxlength="500"
                ></textarea>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeRefundModal">
                Cancelar
              </button>
              <button
                type="button"
                class="btn btn-info text-white"
                :disabled="submittingRefund"
                @click="handleRefund"
              >
                <span v-if="submittingRefund">
                  <i class="fa-solid fa-spinner fa-spin me-1"></i> Processando...
                </span>
                <span v-else>
                  <i class="fa-solid fa-rotate-left me-1"></i>
                  Confirmar Reembolso
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal: Cancelar Assinatura -->
      <div
        v-if="showCancelModal"
        class="modal fade show d-block"
        tabindex="-1"
        style="background-color: rgba(0,0,0,0.5)"
      >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header bg-danger text-white">
              <h5 class="modal-title">
                <i class="fa-solid fa-triangle-exclamation me-2"></i>
                Cancelar Assinatura
              </h5>
              <button type="button" class="btn-close btn-close-white" @click="closeCancelModal"></button>
            </div>
            <div class="modal-body">
              <div class="alert alert-warning">
                <strong>Atenção!</strong> Esta ação irá cancelar a assinatura
                <strong>{{ item.formatted_plan_frequency }}</strong> do usuário
                <strong>{{ item.user && item.user.name }}</strong>.
              </div>
              <ul class="mb-3">
                <li>A assinatura será cancelada no Stripe</li>
                <li>O usuário <strong>não será mais cobrado</strong> nos próximos ciclos</li>
                <li>O acesso continua até o <strong>fim do período atual</strong> ({{ item.formatted_current_period_end }})</li>
              </ul>
              <div class="form-check mt-4">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="confirmCancelCheckbox"
                  v-model="confirmCancel"
                />
                <label class="form-check-label" for="confirmCancelCheckbox">
                  <strong>Eu li e entendi</strong> as consequências desta ação
                </label>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeCancelModal">
                Voltar
              </button>
              <button
                type="button"
                class="btn btn-danger"
                :disabled="!confirmCancel || submittingCancel"
                @click="handleCancelSubscription"
              >
                <span v-if="submittingCancel">
                  <i class="fa-solid fa-spinner fa-spin me-1"></i> Cancelando...
                </span>
                <span v-else>
                  <i class="fa-solid fa-ban me-1"></i> Confirmar Cancelamento
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal: Dar Crédito Financeiro -->
      <div
        v-if="showGrantCreditModal"
        class="modal fade show d-block"
        tabindex="-1"
        style="background-color: rgba(0,0,0,0.5)"
      >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                <i class="fa-solid fa-coins me-2 text-warning"></i>
                Dar Crédito Financeiro
              </h5>
              <button type="button" class="btn-close" @click="closeGrantCreditModal"></button>
            </div>
            <div class="modal-body">
              <div class="alert alert-info mb-3">
                <i class="fa-solid fa-info-circle me-2"></i>
                Esta ação irá criar um <strong>crédito no Stripe</strong> que será descontado
                automaticamente da <strong>próxima parcela</strong> do usuário.
                Caso o crédito seja maior que a parcela, o saldo restante será usado nas faturas seguintes.
              </div>
              <div class="mb-3">
                <label class="form-label">Valor do crédito (R$)</label>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="grantCreditForm.amount"
                  min="0.01"
                  step="0.01"
                  placeholder="Ex: 39.90"
                />
              </div>
              <div
                v-if="grantCreditForm.amount > 0 && creditExceedsSubscription"
                class="alert alert-warning mb-3"
              >
                <i class="fa-solid fa-exclamation-triangle me-2"></i>
                <strong>Atenção:</strong> O crédito ({{ formatCurrency(grantCreditForm.amount) }})
                é maior que o valor da parcela ({{ item.formatted_unit_amount }}).
                O excedente será usado nas próximas faturas.
              </div>
              <div class="mb-3">
                <label class="form-label d-flex justify-content-between">
                  <span>Descrição (opcional)</span>
                  <small class="text-muted">{{ (grantCreditForm.description || '').length }}/250</small>
                </label>
                <input
                  type="text"
                  class="form-control"
                  v-model="grantCreditForm.description"
                  placeholder="Ex: Crédito por problema técnico"
                  maxlength="250"
                />
              </div>
              <div v-if="grantCreditForm.amount > 0" class="text-muted small">
                <i class="fa-solid fa-user me-1"></i>
                Crédito para: <strong>{{ item.user?.name }}</strong>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="closeGrantCreditModal">
                Cancelar
              </button>
              <button
                type="button"
                class="btn btn-warning"
                :disabled="!grantCreditForm.amount || grantCreditForm.amount <= 0 || submittingGrantCredit"
                @click="handleGrantCredit"
              >
                <span v-if="submittingGrantCredit">
                  <i class="fa-solid fa-spinner fa-spin me-1"></i> Processando...
                </span>
                <span v-else>
                  <i class="fa-solid fa-check me-1"></i> Confirmar
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Container>
  </div>
</template>

<script>
import SubscriptionDetailController from './SubscriptionDetailController';
export default SubscriptionDetailController;
</script>

<style scoped>
.info-box {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 12px 16px;
}

.info-box small {
  font-size: 0.75rem;
  margin-bottom: 4px;
}

.text-plus {
  color: #2563eb !important;
}

.period-card {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 12px 16px;
  border: 1px solid #e9ecef;
}

.invoice-credit-detail {
  margin-top: 4px;
  line-height: 1.3;
}

.invoice-credit-detail small {
  font-size: 0.75rem;
  display: block;
}

.refund-line {
  font-size: 0.75rem;
  margin-top: 2px;
  font-weight: 500;
  line-height: 1.2;
}

.nf-icon-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 10px;
  border-radius: 6px;
  color: #dc3545;
  background-color: #fef2f2;
  text-decoration: none;
  transition: all 0.15s;
  font-size: 0.8rem;
  font-weight: 600;
}

.nf-icon-link:hover {
  background-color: #fee2e2;
  color: #b91c1c;
}

.nf-icon-link.nf-icon-xml {
  color: #6c757d;
  background-color: #f8f9fa;
}

.nf-icon-link.nf-icon-xml:hover {
  background-color: #e9ecef;
  color: #495057;
}

.nf-icon-link.nf-icon-stripe {
  color: #635bff;
  background-color: #f3f2ff;
  font-size: 1.1rem;
  padding: 0 12px;
}

.nf-icon-link.nf-icon-stripe:hover {
  background-color: #e4e2ff;
  color: #4f47e0;
}

.refund-summary {
  background-color: #ecfeff;
  border-radius: 8px;
  padding: 12px 16px;
  border: 1px solid #a5f3fc;
}

.nf-icon-link.nf-icon-refund {
  color: #0dcaf0;
  background-color: #ecfeff;
  border: none;
  cursor: pointer;
}

.nf-icon-link.nf-icon-refund:hover {
  background-color: #cffafe;
  color: #0891b2;
}

.breakdown {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 12px 16px;
}

.breakdown-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.breakdown .table {
  background-color: transparent;
}

.breakdown .table td {
  border-bottom: 1px solid #e9ecef;
}

.breakdown .total-row td {
  border-top: 2px solid #dee2e6;
  border-bottom: none;
  font-size: 1.05rem;
}
</style>
